/**
 * <s-product-thumbs> — PDP thumbnail navigation for the main photo slider.
 *
 * Required markup:
 *   [data-product-gallery]        — horizontally scrolling main slider (position: relative).
 *   [data-gallery-slide]          — each slide inside the slider, in order.
 *   button[data-thumb-index="n"]  — thumbnail that shows slide n (0-based).
 */
class SProductThumbs extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const gallery = this.querySelector('[data-product-gallery]');
    if (!gallery) return;

    this.addEventListener('click', this.#handleClick, { signal: this.#ac.signal });

    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#setActive(slides.indexOf(entry.target));
        }
      },
      { root: gallery, threshold: 0.6 },
    );
    for (const slide of slides) this.#observer.observe(slide);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-thumb-index]');
    if (!thumb || !this.contains(thumb)) return;

    const gallery = this.querySelector('[data-product-gallery]');
    const index = Number(thumb.getAttribute('data-thumb-index'));
    const slide = gallery?.querySelectorAll('[data-gallery-slide]')[index];
    if (!gallery || !slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    gallery.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #setActive(index) {
    if (index < 0) return;
    for (const thumb of this.querySelectorAll('[data-thumb-index]')) {
      const active = Number(thumb.getAttribute('data-thumb-index')) === index;
      thumb.toggleAttribute('aria-current', active);
      if (active) thumb.setAttribute('aria-current', 'true');
    }
  }
}

if (!customElements.get('s-product-thumbs')) {
  customElements.define('s-product-thumbs', SProductThumbs);
}
